import {
    useCallback,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { useLatestRef } from './use-latest-ref.ts';

type ChangeHandler<T> = (state: T) => void;
type SetStateFn<T> = React.Dispatch<React.SetStateAction<T>>;

export type UseControllableStateParams<T> = {
    value?: T;
    defaultValue: T;
    onChange?: ChangeHandler<T>;
};

/**
 * Hook for building an optionally-controlled component state, mostly copied
 * from Radix.
 */
export function useControllableState<T>({
    value: prop,
    defaultValue,
    onChange,
}: UseControllableStateParams<T>): [T, SetStateFn<T>] {
    const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
        defaultValue,
        onChange,
    });
    const onChangeRef = useLatestRef(onChange);
    const isControlled = prop !== undefined;
    const value = isControlled ? prop : uncontrolledProp;

    const setValue = useCallback<SetStateFn<T>>(
        (nextValue) => {
            if (isControlled) {
                const value = isFunction(nextValue) ? nextValue(prop) : nextValue;
                if (value !== prop) {
                    onChangeRef.current?.(value);
                }
            } else {
                setUncontrolledProp(nextValue);
            }
        },
        [isControlled, prop, setUncontrolledProp, onChangeRef],
    );

    return [value, setValue];
}

type UseUncontrolledStateParams<T> = {
    defaultValue: T;
    onChange?: ChangeHandler<T>;
};

function useUncontrolledState<T>({
    defaultValue: defaultProp,
    onChange,
}: UseUncontrolledStateParams<T>): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState(defaultProp);
    const prevValueRef = useRef(value);

    const handleOnChange = useEffectEvent((value: T) => {
        onChange?.(value);
    });

    useEffect(() => {
        if (prevValueRef.current !== value) {
            handleOnChange(value);
            prevValueRef.current = value;
        }
    }, [value, prevValueRef]);

    return [value, setValue];
}

function isFunction(value: unknown): value is (...args: any[]) => any {
    return typeof value === 'function';
}
